// THE PHONE VIEW (Session BDZ, Director request, 20 October 2026). On a laptop, an admin can see the office and
// engineer apps at phone size inside the browser: the current page is shown in a same-origin iframe, so the app
// sees a true phone width and its own media queries fire, and a normal screenshot of the frame reads as a phone
// screen. UI only: no API, no tool, no migration, no data. This file holds every rule that is not drawing.
//
// THE SAFETY RULES, each pinned by phonePreview.test.ts:
//   - WHO SEES IT: an admin only, on a screen wider than 900px, never inside the frame itself (no recursion).
//     The engineer routes admit admin and engineer today (App.tsx ProtectedRoute), so the office role cannot open
//     them and is not offered the control; the frame uses the admin's OWN session, never an engineer's. Nothing here
//     signs anyone in, creates a session or impersonates.
//   - A PAGE IN THE FRAME CANNOT WRITE, except on a local development server. An admin walking the engineer app is
//     not an engineer, and a click inside a review frame must never raise a job, send an email or touch Xero. Every
//     request that is not a read is refused in api.ts (rawRequest and the download helpers), and the offline stores
//     inside the frame are a separate database (db.ts), so a sheet or photo queued there is never replayed by the
//     real engineer app on this device.
//   - NOTHING IS STORED BUT TWO NUMBERS: the last width and text size (localStorage, in a try and catch).

import type { Role } from "./api";

/** The frame's window name. The app inside reads it (with "I am in a frame") to know it is being previewed. */
export const PHONE_VIEW_FRAME_NAME = "ele-phone-view";

/** The control is offered only on a screen wider than this, in CSS pixels. */
export const PHONE_VIEW_MIN_VIEWPORT = 900;

/** Roles offered the control. Admin only, by the Director's ruling of 20 October 2026. */
export const PHONE_VIEW_ROLES: readonly Role[] = ["admin"];

/** The roles the engineer route tree admits, mirroring App.tsx ProtectedRoute allow={["admin", "engineer"]}. */
export const ENGINEER_ROUTE_ROLES: readonly Role[] = ["admin", "engineer"];

export interface PhonePreset {
  width: number;
  height: number;
}

/** Portrait presets. The default is 390 by 844. */
export const PHONE_PRESETS: readonly PhonePreset[] = [
  { width: 360, height: 800 },
  { width: 375, height: 812 },
  { width: 390, height: 844 },
  { width: 414, height: 896 },
];
export const DEFAULT_PHONE_WIDTH = 390;

/** System text size, as a percentage of the normal size. */
export const PHONE_TEXT_SCALES = [100, 125, 150] as const;
export type PhoneTextScale = (typeof PHONE_TEXT_SCALES)[number];
export const DEFAULT_PHONE_TEXT_SCALE: PhoneTextScale = 100;

/** The engineer app's own routes (the five tabs), for the route switcher. My Calendar is /engineer/diary. */
export const ENGINEER_ROUTES: readonly { label: string; path: string }[] = [
  { label: "Home", path: "/engineer" },
  { label: "My Jobs", path: "/engineer/jobs" },
  { label: "My Calendar", path: "/engineer/diary" },
  { label: "Timesheet", path: "/engineer/timesheet" },
  { label: "Messages", path: "/engineer/messages" },
];

/** Messages between the stage and the frame. Same origin only, and only from the frame's own window. */
export const PHONE_VIEW_MSG = {
  /** frame to stage: the app is ready (so the stage sends the text size). */
  ready: "ele-phone-view:ready",
  /** frame to stage: where the app now is. */
  path: "ele-phone-view:path",
  /** stage to frame: the text size. */
  textScale: "ele-phone-view:text-scale",
  /** stage to frame: go to a path (an in-app navigation, no reload). */
  navigate: "ele-phone-view:navigate",
} as const;

/** Raised in the frame when a write is refused, and only when a person has just done something. */
export const PHONE_VIEW_BLOCKED_EVENT = "ele:phone-view-write-blocked";

/** The plain words shown to the person, and thrown as the refusal. No double hyphen, no em dash. */
export const PHONE_VIEW_BLOCKED_MESSAGE = "Preview only. Saving is switched off in Phone view.";

/** The body carried on the refusal, so a screen that shows a server's own message shows this one and not its guess at a 403. */
export const PHONE_VIEW_BLOCKED_BODY = { error: "phone_view_read_only", message: PHONE_VIEW_BLOCKED_MESSAGE } as const;

export const PHONE_VIEW_STORAGE_KEY = "ele.phoneView.v1";

/** The remembered on/off state (Session BEA): whether the Phone view is on, and the page its frame was last on. */
export const PHONE_VIEW_ON_STORAGE_KEY = "ele.phoneView.on.v1";

// ---------------------------------------------------------------------------------------------------------------
// Who is offered it
// ---------------------------------------------------------------------------------------------------------------

export function phoneViewAvailable(input: { role: Role | undefined; viewportWidth: number; inFrame: boolean }): boolean {
  if (input.inFrame) return false;
  if (!input.role || !PHONE_VIEW_ROLES.includes(input.role)) return false;
  return input.viewportWidth > PHONE_VIEW_MIN_VIEWPORT;
}

/** Can this account open the engineer app with its own session? When not, the stage says so and offers the office app only. */
export function engineerRoutesAvailable(role: Role | undefined): boolean {
  return !!role && ENGINEER_ROUTE_ROLES.includes(role);
}

// ---------------------------------------------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------------------------------------------

const FRAMEABLE_TREES = ["/office", "/engineer"];
const UNFRAMED_PAGES = ["/engineer/login", "/engineer/install", "/engineer/offline"];

/**
 * A path the frame may open, or null. Only the office and engineer trees (the only pages the server lets the app
 * frame), written as a path on this origin: a full address of this site is accepted and reduced to its path, an
 * address on another site, a protocol-relative one, an /api path and anything outside the two trees is not.
 */
export function normalisePhonePath(input: string, origin: string): string | null {
  const text = input.trim();
  if (!text || /[\u0000-\u001f\u007f\\]/.test(text)) return null;
  let url: URL;
  try {
    url = new URL(text, `${origin}/`);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  // A protocol-relative or backslash trick resolves to another origin above; this also catches "//office".
  if (text.startsWith("//")) return null;
  const pathname = url.pathname.replace(/\/{2,}/g, "/");
  const inTree = FRAMEABLE_TREES.some((tree) => pathname === tree || pathname.startsWith(`${tree}/`));
  if (!inTree) return null;
  // The engineer sign-in, install and offline pages are never opened by the stage: it must not become a way to sign in.
  if (UNFRAMED_PAGES.includes(pathname.toLowerCase().replace(/\/$/, ""))) return null;
  return `${pathname}${url.search}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Settings (the last width and text size)
// ---------------------------------------------------------------------------------------------------------------

export interface PhoneViewSettings {
  width: number;
  textScale: PhoneTextScale;
}

export const DEFAULT_PHONE_SETTINGS: PhoneViewSettings = { width: DEFAULT_PHONE_WIDTH, textScale: DEFAULT_PHONE_TEXT_SCALE };

export function presetFor(width: number): PhonePreset {
  return PHONE_PRESETS.find((p) => p.width === width) ?? PHONE_PRESETS.find((p) => p.width === DEFAULT_PHONE_WIDTH)!;
}

function sanitiseSettings(raw: unknown): PhoneViewSettings {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PHONE_SETTINGS };
  const o = raw as { width?: unknown; textScale?: unknown };
  const width = PHONE_PRESETS.some((p) => p.width === o.width) ? (o.width as number) : DEFAULT_PHONE_WIDTH;
  const textScale = (PHONE_TEXT_SCALES as readonly unknown[]).includes(o.textScale) ? (o.textScale as PhoneTextScale) : DEFAULT_PHONE_TEXT_SCALE;
  return { width, textScale };
}

/** Never throws: storage can be missing, blocked or hold rubbish (private window, cleared site data). */
export function loadPhoneViewSettings(): PhoneViewSettings {
  try {
    const raw = window.localStorage.getItem(PHONE_VIEW_STORAGE_KEY);
    return sanitiseSettings(raw ? (JSON.parse(raw) as unknown) : null);
  } catch {
    return { ...DEFAULT_PHONE_SETTINGS };
  }
}

export function savePhoneViewSettings(settings: PhoneViewSettings): void {
  try {
    window.localStorage.setItem(PHONE_VIEW_STORAGE_KEY, JSON.stringify(sanitiseSettings(settings)));
  } catch {
    // Storage unavailable: the choice still holds for this page's lifetime.
  }
}

// ---------------------------------------------------------------------------------------------------------------
// The remembered on/off state (Session BEA)
// ---------------------------------------------------------------------------------------------------------------

export interface PhoneViewOn {
  on: boolean;
  /** The page the frame was last on, kept only when the frame may open it; null otherwise. */
  path: string | null;
}

/** Never throws: storage can be missing, blocked or hold rubbish. Anything unreadable means off. */
export function loadPhoneViewOn(): PhoneViewOn {
  try {
    const raw = window.localStorage.getItem(PHONE_VIEW_ON_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as { on?: unknown; path?: unknown } | null) : null;
    if (!parsed || typeof parsed !== "object" || parsed.on !== true) return { on: false, path: null };
    const path = typeof parsed.path === "string" ? normalisePhonePath(parsed.path, window.location.origin) : null;
    return { on: true, path };
  } catch {
    return { on: false, path: null };
  }
}

export function savePhoneViewOn(value: PhoneViewOn): void {
  try {
    window.localStorage.setItem(PHONE_VIEW_ON_STORAGE_KEY, JSON.stringify({ on: value.on === true, path: value.path }));
  } catch {
    // Storage unavailable: the Phone view still works for this page's lifetime, it just will not come back after a refresh.
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Geometry and the text for the clipboard
// ---------------------------------------------------------------------------------------------------------------

/** The frame's CSS size. Landscape swaps the preset's two sides. */
export function frameSize(width: number, landscape: boolean): { width: number; height: number } {
  const preset = presetFor(width);
  return landscape ? { width: preset.height, height: preset.width } : { width: preset.width, height: preset.height };
}

/** The scale that makes the frame fit the room the stage has, never above 1 and never so small it is useless. */
export function fitScale(available: { width: number; height: number }, size: { width: number; height: number }): number {
  if (available.width <= 0 || available.height <= 0) return 1;
  const scale = Math.min(1, available.width / size.width, available.height / size.height);
  // Rounded DOWN, so the frame never overshoots the room it was fitted to and raises a stray scrollbar.
  return Math.max(0.35, Math.floor(scale * 100) / 100);
}

/** "/engineer/diary at 390px, text 100%, light" for pasting beside a screenshot. */
export function copySummary(input: { path: string; width: number; textScale: number; theme: "light" | "dark"; landscape?: boolean }): string {
  const rotated = input.landscape ? ", landscape" : "";
  return `${input.path} at ${input.width}px, text ${input.textScale}%, ${input.theme}${rotated}`;
}

// ---------------------------------------------------------------------------------------------------------------
// Inside the frame
// ---------------------------------------------------------------------------------------------------------------

/** True only in the stage's own iframe: framed, and named as the stage names it. */
export function isInPhonePreviewFrame(win: { self: unknown; top: unknown; name: string } | undefined = typeof window === "undefined" ? undefined : window): boolean {
  if (!win) return false;
  try {
    return win.self !== win.top && win.name === PHONE_VIEW_FRAME_NAME;
  } catch {
    return false;
  }
}

function isLocalHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h === "::1" || h.endsWith(".localhost");
}

/**
 * May a page in the frame write? Only on a local development server (Vite's dev mode on this machine, where the data
 * is the repo's own seed). Anywhere else, staging included, the answer is no: staging is treated like production
 * because nothing here can tell its data from real data.
 */
export function previewWritesAllowed(env: { dev: boolean; hostname: string }): boolean {
  return env.dev && isLocalHostname(env.hostname);
}

const READ_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * Sign-in and the public routes are never reachable from the frame, even on a local development server: a sign-in there
 * would replace the browser-wide session cookie with another account's (an engineer's), which is exactly what the Phone
 * view must never do. `path` is the API path without the /api prefix.
 */
export function isSessionPath(path: string | undefined): boolean {
  return !!path && (path === "/auth" || path.startsWith("/auth/") || path.startsWith("/public/"));
}

/** Is this request refused because it is a write made from inside the Phone view? */
export function phoneViewBlocksWrite(method: string | undefined, path?: string): boolean {
  if (!isInPhonePreviewFrame()) return false;
  if (!method || READ_METHODS.has(method.toUpperCase())) return false;
  if (isSessionPath(path)) return true;
  return !previewWritesAllowed({ dev: import.meta.env.DEV === true, hostname: window.location.hostname });
}

// A refusal only becomes a visible notice when a person has just done something. The offline stores retry on timers,
// and a notice every couple of minutes from a queue nobody touched would be noise.
let lastGestureAt = 0;
export const GESTURE_WINDOW_MS = 3000;
export function noteGesture(now = Date.now()): void {
  lastGestureAt = now;
}
export function recentGesture(now = Date.now()): boolean {
  return now - lastGestureAt <= GESTURE_WINDOW_MS;
}

/** Tell the frame's notice a write was refused. */
export function announcePhoneViewWriteBlocked(): void {
  if (typeof window === "undefined" || !recentGesture()) return;
  window.dispatchEvent(new Event(PHONE_VIEW_BLOCKED_EVENT));
}

/** The name of the offline database: its own inside the frame, so queued work there is never replayed by the real app. */
export function offlineDatabaseName(base: string): string {
  return isInPhonePreviewFrame() ? `${base}-phone-view` : base;
}

/** The suffix for a localStorage key that holds device-side queues. */
export function storageKeySuffix(): string {
  return isInPhonePreviewFrame() ? ".phoneView" : "";
}
